import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ruleEvents as ruleEventsApi } from '@/lib/trackizy';
import { buildReplayEvents } from '@/features/replay/replayEvents';
import { selectGeofencesOnly, selectPOIs } from '@/store/selectors';

/**
 * Compute the forensic event array for an active mini-replay.
 *
 * @param {object|null} miniReplay  { deviceId, deviceName, positions } or null
 * @returns {Array} normalized ReplayEvent[] (empty until positions + fetch settle)
 */
export function useReplayEvents(miniReplay) {
  const [ruleRows, setRuleRows] = useState([]);
  const positions = miniReplay?.positions;
  const deviceId = miniReplay?.deviceId;

  // Geofences: selectGeofencesOnly returns array (Object.values filtered for !attributes?.poi)
  // POIs: selectPOIs returns array (Object.values of state.pois.items)
  // OrgId: state.org.id — confirmed by orgSlice initialState + POIForm/ImportPOIsDialog usage
  const geofences = useSelector(selectGeofencesOnly) || [];
  const pois = useSelector(selectPOIs) || [];
  const orgId = useSelector((s) => s.org?.id);

  // window bounds from the loaded positions
  const from = positions?.length ? positions[0].fixTime : null;
  const to = positions?.length ? positions[positions.length - 1].fixTime : null;

  useEffect(() => {
    let cancelled = false;
    if (!deviceId || !from || !to) { setRuleRows([]); return; }
    ruleEventsApi
      .list({ deviceId, from, to, limit: 1000 })
      .then((rows) => { if (!cancelled) setRuleRows(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setRuleRows([]); });
    return () => { cancelled = true; };
  }, [deviceId, from, to]);

  return useMemo(
    () => buildReplayEvents({ positions, ruleEvents: ruleRows, geofences, pois, orgId }),
    [positions, ruleRows, geofences, pois, orgId],
  );
}
